import dayjs from "dayjs";
import { useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import toastr from "toastr";
import {
  useApplyForLeave,
  useApproveWorkerBookingRequest,
  useGetWorkerBookingRequestDetails,
} from "../../../api/booking";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Spinners from "../../../components/Common/Spinner";
import {
  LEAVE_REASONS,
  LEAVE_REASONS_LABELS,
} from "../../../constants/booking";
import BookingAgreementSignModal from "../components/BookingAgreementSignModal";

const LEAVE_REASON_I18N_KEYS = {
  [LEAVE_REASONS.SICK]: "leaveReasonSick",
  [LEAVE_REASONS.FAMILY_EMERGENCY]: "leaveReasonFamilyEmergency",
  [LEAVE_REASONS.PERSONAL]: "leaveReasonPersonal",
  [LEAVE_REASONS.MEDICAL_APPOINTMENT]: "leaveReasonMedicalAppointment",
  [LEAVE_REASONS.BEREAVEMENT]: "leaveReasonBereavement",
  [LEAVE_REASONS.OTHER]: "leaveReasonOther",
};

const getLeaveReasonLabel = (reason, t) => {
  const key = LEAVE_REASON_I18N_KEYS[reason];
  return key ? t(key, LEAVE_REASONS_LABELS[reason]) : LEAVE_REASONS_LABELS[reason];
};

const formatBreakDuration = (breakTime) => {
  if (!breakTime) return "-";
  const parts = String(breakTime).split(":");
  const hours = Number(parts[0]) || 0;
  const minutes = Number(parts[1]) || 0;
  const totalMinutes = hours * 60 + minutes;
  if (totalMinutes <= 0) return "-";
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
};

const resolveRequestShiftInfo = (requestData) => {
  if (requestData?.shift) return requestData.shift;
  const shiftIndex = requestData?.shiftIndex;
  const patterns = requestData?.booking?.shiftPatterns;
  if (Array.isArray(patterns) && typeof shiftIndex === "number") {
    const match = patterns.find((p) => p.shiftIndex === shiftIndex);
    if (match) return match;
  }
  return requestData?.booking?.inviteShiftPattern ?? null;
};

const canWorkerAcceptInviteDate = (bookingRequest, shiftDate) => {
  const invite = bookingRequest?.booking?.inviteShiftPattern;
  if (!invite || invite.smartMatchingActiveMode !== "agreement") {
    return true;
  }
  const dateRow = invite.dates?.find((d) => d.date === shiftDate);
  return (dateRow?.remaining ?? 0) > 0;
};

const WorkerBookingRequestDetails = ({ t }) => {
  document.title = t("workerBookingRequestPageTitle", "Booking Request | Worker");

  const { requestSlug } = useParams();
  const { data, isLoading, error, refetch } =
    useGetWorkerBookingRequestDetails(requestSlug);
  const approveRequest = useApproveWorkerBookingRequest();
  const applyForLeave = useApplyForLeave();

  const [dateDecisions, setDateDecisions] = useState({});
  const [showDecisionModal, setShowDecisionModal] = useState(false);

  // --- Leave Application Logic ---
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveDates, setLeaveDates] = useState([]);
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveDescription, setLeaveDescription] = useState("");
  const [showSignAgreementModal, setShowSignAgreementModal] = useState(false);

  const requestData = data?.data?.data ?? data?.data ?? data;
  const canSignAgreement =
    requestData?.markAsConfirmedByAdmin === true &&
    Boolean(requestData?.agreementPdf) &&
    requestData?.isAgreementSubmitted === false;

  // Get shifts that are confirmed by admin and can have leave applied
  const confirmedShifts =
    requestData?.WorkerShift?.filter(
      (shift) => shift.markAsConfirmedByAdmin && !shift.leaveApply
    ) || [];
  const hasConfirmedShifts = confirmedShifts.length > 0;

  // Check if there are any pending dates that can be edited
  const hasPendingDates = requestData?.WorkerShift?.some(
    (shift) =>
      shift.status === "pending" &&
      requestData.status === "pending" &&
      !dayjs(shift.expiresAt).isBefore(dayjs())
  );

  const getStatusBadge = (status) => {
    const v = String(status ?? "").toLowerCase();
    switch (v) {
      case "pending":
        return (
          <Badge className="bg-warning text-dark">
            {t("workerBookingStatusPending", "Pending")}
          </Badge>
        );
      case "accepted":
        return (
          <Badge className="bg-success">
            {t("workerBookingStatusAccepted", "Accepted")}
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-danger">
            {t("workerBookingStatusRejected", "Rejected")}
          </Badge>
        );
      default:
        return <Badge className="bg-secondary">{status || "-"}</Badge>;
    }
  };

  const handleDateDecision = (date, status) => {
    setDateDecisions((prev) => ({
      ...prev,
      [date]: status,
    }));
  };

  const openDecisionModal = () => {
    // Initialize decisions for pending dates only
    if (requestData?.WorkerShift) {
      const initialDecisions = {};
      requestData.WorkerShift.forEach((shift) => {
        if (
          shift.status === "pending" &&
          requestData.status === "pending" &&
          !dayjs(shift.expiresAt).isBefore(dayjs())
        ) {
          initialDecisions[shift.date] = canWorkerAcceptInviteDate(
            requestData,
            shift.date
          )
            ? "pending"
            : "rejected";
        }
      });
      setDateDecisions(initialDecisions);
    }
    setShowDecisionModal(true);
  };

  const handleSubmitDecisions = () => {
    // Build the payload with all date decisions
    const dates = Object.keys(dateDecisions)
      .filter((date) => {
        // Only include dates that have been changed from pending
        const shift = requestData.WorkerShift.find((s) => s.date === date);
        return (
          shift &&
          shift.status === "pending" &&
          dateDecisions[date] !== "pending"
        );
      })
      .map((date) => ({
        date,
        status: dateDecisions[date],
      }));

    const invalidAccept = dates.some(
      (d) =>
        d.status === "accepted" &&
        !canWorkerAcceptInviteDate(requestData, d.date)
    );
    if (invalidAccept) {
      toastr.error(
        t(
          "workerBookingRequestAcceptUnavailable",
          "Accept is not available for one or more dates with no remaining slots."
        )
      );
      return;
    }

    // Check if there are any decisions to submit
    if (dates.length === 0) {
      toastr.error(
        t(
          "workerBookingRequestSelectOneDateDecision",
          "Please accept or reject at least one date"
        )
      );
      return;
    }

    approveRequest.mutate(
      { requestSlug, payload: { dates } },
      {
        onSuccess: () => {
          toastr.success(
            t(
              "workerBookingRequestUpdatedSuccess",
              "Booking request updated successfully!"
            )
          );
          if (dates.some((d) => d.status === "accepted")) {
            toastr.info(
              t(
                "workerBookingRequestAgreementComing",
                "You will shortly receive an agreement to sign."
              )
            );
          }
          setShowDecisionModal(false);
          refetch();
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title={t("workerBookingRequestBreadcrumbBookings", "Bookings")}
            breadcrumbItem={t(
              "workerBookingRequestBreadcrumbDetails",
              "Booking Request Details"
            )}
          />
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title={t("workerBookingRequestBreadcrumbBookings", "Bookings")}
            breadcrumbItem={t(
              "workerBookingRequestBreadcrumbDetails",
              "Booking Request Details"
            )}
          />
          <div className="alert alert-danger">
            {t("workerBookingRequestLoadError", "Error loading booking request details: {{message}}", {
              message:
                error.message ||
                t("workerBookingRequestUnknownError", "Unknown error"),
            })}
          </div>
        </div>
      </div>
    );
  }

  if (!requestData) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title={t("workerBookingRequestBreadcrumbBookings", "Bookings")}
            breadcrumbItem={t(
              "workerBookingRequestBreadcrumbDetails",
              "Booking Request Details"
            )}
          />
          <div className="text-center py-5">
            <div className="text-muted">
              <i
                className="mdi mdi-information-outline"
                style={{ fontSize: "48px" }}
              ></i>
              <p className="mt-3">
                {t("workerBookingRequestNotFound", "Booking request not found.")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const openLeaveModal = () => {
    setLeaveDates([]);
    setLeaveReason("");
    setLeaveDescription("");
    setShowLeaveModal(true);
  };

  const toggleLeaveDate = (date) => {
    setLeaveDates((prev) => {
      if (prev.includes(date)) {
        return prev.filter((d) => d !== date);
      }
      return [...prev, date];
    });
  };

  const handleSubmitLeave = () => {
    if (leaveDates.length === 0) {
      toastr.error(
        t(
          "workerBookingRequestSelectOneDate",
          "Please select at least one date"
        )
      );
      return;
    }
    if (!leaveReason) {
      toastr.error(
        t("workerBookingRequestSelectReasonToast", "Please select a reason")
      );
      return;
    }

    const payload = {
      dates: leaveDates,
      reason: leaveReason,
      reasonDescription: leaveDescription,
    };

    applyForLeave.mutate(
      { bookingRequestSlug: requestSlug, payload },
      {
        onSuccess: () => {
          toastr.success(
            t(
              "workerBookingRequestLeaveSuccess",
              "Leave request submitted successfully!"
            )
          );
          setShowLeaveModal(false);
          // Ideally refetch or update local state if needed
          // refetch();
        },
        onError: (err) => {
          // Toast handled global but good to maintain structure
        },
      }
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={t("workerBookingRequestBreadcrumbBookings", "Bookings")}
          breadcrumbItem={t(
            "workerBookingRequestBreadcrumbDetails",
            "Booking Request Details"
          )}
        />

        {/* Request Overview */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h5 className="mb-0">
              {t("workerBookingRequestOverview", "Request Overview")}
            </h5>
          </CardHeader>
          <CardBody>
            <Row className="gy-3">
              <Col md={3}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestId", "Request ID")}
                </small>
                <div className="fw-medium">{requestData.slug || "-"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestBookingId", "Booking ID")}
                </small>
                <div className="fw-medium">
                  {requestData.booking?.slug || "-"}
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestStatus", "Status")}
                </small>
                <div>{getStatusBadge(requestData.status)}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestCreatedDate", "Created Date")}
                </small>
                <div>
                  {requestData.createdAt
                    ? dayjs(requestData.createdAt).format("DD MMM YYYY, HH:mm")
                    : "-"}
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestRole", "Role")}
                </small>
                <div className="fw-medium">
                  {requestData.role?.title || "-"}
                </div>
              </Col>
              {requestData.respondedAt && (
                <Col md={3}>
                  <small className="text-muted d-block">
                    {t("workerBookingRequestRespondedAt", "Responded At")}
                  </small>
                  <div>
                    {dayjs(requestData.respondedAt).format(
                      "DD MMM YYYY, HH:mm"
                    )}
                  </div>
                </Col>
              )}
              {requestData.decisionReason && (
                <Col md={6}>
                  <small className="text-muted d-block">
                    {t("workerBookingRequestDecisionReason", "Decision Reason")}
                  </small>
                  <div>{requestData.decisionReason}</div>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Booking Information */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h5 className="mb-0">
              {t("workerBookingRequestBookingInfo", "Booking Information")}
            </h5>
          </CardHeader>
          <CardBody>
            <Row className="gy-2">
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestClient", "Client")}
                </small>
                <div className="fw-medium">
                  {requestData.booking?.createdBy?.fullName || "-"}
                </div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestClientEmail", "Client Email")}
                </small>
                <div>{requestData.booking?.createdBy?.email || "-"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestClientPhone", "Client Phone")}
                </small>
                <div>{requestData.booking?.createdBy?.phoneNumber || "-"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestBookingType", "Booking Type")}
                </small>
                <div>
                  {requestData.booking?.bookingType === "urgent" ? (
                    <Badge className="bg-danger">
                      {t("workerBookingRequestUrgent", "Urgent")}
                    </Badge>
                  ) : (
                    <Badge className="bg-primary">
                      {t("workerBookingRequestNotUrgent", "Not Urgent")}
                    </Badge>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Location Information */}
        <Card className="mb-3">
          <CardHeader className="bg-primary text-white">
            <h5 className="mb-0">
              {t("workerBookingRequestLocationInfo", "Location Information")}
            </h5>
          </CardHeader>
          <CardBody>
            <Row className="gy-2">
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestSiteName", "Site Name")}
                </small>
                <div className="fw-medium">
                  {requestData.booking?.location?.siteName || "-"}
                </div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestSearchAddress", "Search Address")}
                </small>
                <div>{requestData.booking?.location?.searchAddress || "-"}</div>
              </Col>
              <Col md={12}>
                <small className="text-muted d-block">
                  {t("workerBookingRequestFullAddress", "Full Address")}
                </small>
                <div>{requestData.booking?.location?.fullAddress || "-"}</div>
              </Col>
              {requestData.booking?.location?.latitude &&
                requestData.booking?.location?.longitude && (
                  <Col md={12}>
                    <small className="text-muted d-block">
                      {t("workerBookingRequestCoordinates", "Coordinates")}
                    </small>
                    <div>
                      {requestData.booking.location.latitude},{" "}
                      {requestData.booking.location.longitude}
                    </div>
                  </Col>
                )}
            </Row>
          </CardBody>
        </Card>

        {/* Notes */}
        {requestData.booking?.notes && (
          <Card className="mb-3">
            <CardHeader className="bg-primary text-white">
              <h5 className="mb-0">
                {t("workerBookingRequestNotes", "Notes")}
              </h5>
            </CardHeader>
            <CardBody>
              <p className="mb-0">{requestData.booking.notes}</p>
            </CardBody>
          </Card>
        )}

        {/* Shift Details */}
        {(() => {
          const shiftInfo = resolveRequestShiftInfo(requestData);
          if (!shiftInfo) return null;
          return (
            <Card className="mb-3">
              <CardHeader className="bg-primary text-white">
                <h5 className="mb-0">
                  {t("workerBookingRequestShiftDetails", "Shift Details")}
                </h5>
              </CardHeader>
              <CardBody>
                <Row className="gy-3">
                  <Col md={4}>
                    <small className="text-muted d-block">
                      {t("workerBookingRequestStartTime", "Start time")}
                    </small>
                    <div className="fw-medium">
                      {shiftInfo.startTime || "-"}
                    </div>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted d-block">
                      {t("workerBookingRequestEndTime", "End time")}
                    </small>
                    <div className="fw-medium">{shiftInfo.endTime || "-"}</div>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted d-block">
                      {t("workerBookingRequestBreakDuration", "Break duration")}
                    </small>
                    <div className="fw-medium">
                      {formatBreakDuration(shiftInfo.breakTime)}
                    </div>
                  </Col>
                  {shiftInfo.shiftDetails && (
                    <Col md={12}>
                      <small className="text-muted d-block">
                        {t("workerBookingRequestDescription", "Description")}
                      </small>
                      <div>{shiftInfo.shiftDetails}</div>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          );
        })()}

        {/* Worker Shifts */}
        {requestData.WorkerShift && requestData.WorkerShift.length > 0 && (
          <Card className="mb-3">
            <CardHeader className="bg-primary text-white">
              <h5 className="mb-0">
                {t("workerBookingRequestShiftDates", "Shift Dates & Details")}
              </h5>
            </CardHeader>
            <CardBody>
              <div className="table-responsive">
                <Table bordered>
                  <thead className="table-light">
                    <tr>
                      <th>{t("workerBookingRequestDate", "Date")}</th>
                      <th>{t("workerBookingRequestStartTime", "Start time")}</th>
                      <th>{t("workerBookingRequestEndTime", "End time")}</th>
                      <th>
                        {t("workerBookingRequestBreakDuration", "Break duration")}
                      </th>
                      <th>{t("workerBookingRequestStatus", "Status")}</th>
                      <th>
                        {t("workerBookingRequestPerHourPrice", "Per Hour Price")}
                      </th>
                      <th>{t("workerBookingRequestExpiresAt", "Expires At")}</th>
                      <th>
                        {t("workerBookingRequestRespondedAt", "Responded At")}
                      </th>
                      <th>
                        {t("workerBookingRequestDecisionReason", "Decision Reason")}
                      </th>
                      <th>
                        {t("workerBookingRequestAssignedByAdmin", "Assigned by admin")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestData.WorkerShift.map((shift, idx) => {
                      const isExpired = dayjs(shift.expiresAt).isBefore(
                        dayjs()
                      );
                      return (
                        <tr key={idx}>
                          <td>{dayjs(shift.date).format("DD MMM YYYY")}</td>
                          <td>{shift.startTime || "-"}</td>
                          <td>{shift.endTime || "-"}</td>
                          <td>{formatBreakDuration(shift.breakTime)}</td>
                          <td>
                            <div>
                              {getStatusBadge(shift.status)}
                              {isExpired && shift.status === "pending" && (
                                <Badge className="bg-danger ms-1">
                                  {t("workerBookingStatusExpired", "Expired")}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            £{parseFloat(shift.perHourPrice || 0).toFixed(2)}
                          </td>
                          <td>
                            {shift.expiresAt
                              ? dayjs(shift.expiresAt).format(
                                  "DD MMM YYYY, HH:mm"
                                )
                              : "-"}
                          </td>
                          <td>
                            {shift.respondedAt
                              ? dayjs(shift.respondedAt).format(
                                  "DD MMM YYYY, HH:mm"
                                )
                              : "-"}
                          </td>
                          <td>{shift.decisionReason || "-"}</td>
                          <td>
                            {shift.markAsConfirmedByAdmin ? (
                              <Badge className="bg-success">
                                {t("workerBookingYes", "Yes")}
                              </Badge>
                            ) : (
                              <Badge className="bg-secondary">
                                {t("workerBookingNo", "No")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="mt-3 d-flex justify-content-end gap-2">
                {canSignAgreement && (
                  <Button
                    color="primary"
                    outline
                    onClick={() => setShowSignAgreementModal(true)}
                  >
                    <i className="mdi mdi-file-sign me-1"></i>
                    {t("workerBookingRequestSignAgreement", "Sign Agreement")}
                  </Button>
                )}
                {hasConfirmedShifts && (
                  <Button color="warning" onClick={openLeaveModal}>
                    <i className="mdi mdi-calendar-remove me-1"></i>
                    {t("workerBookingRequestApplyLeaves", "Apply for Leaves")}
                  </Button>
                )}
                {hasPendingDates && (
                  <Button color="primary" onClick={openDecisionModal}>
                    <i className="mdi mdi-check me-1"></i>
                    {t("workerBookingRequestSubmitDecision", "Submit Decision")}
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Back Button */}
        <div className="mb-3">
          <Link to="/my-assignments" className="btn btn-secondary">
            <i className="mdi mdi-arrow-left me-1"></i>
            {t("workerBookingRequestBack", "Back")}
          </Link>
        </div>

        {/* Decision Modal */}
        <Modal
          isOpen={showDecisionModal}
          toggle={() => setShowDecisionModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowDecisionModal(false)}>
            {t(
              "workerBookingRequestDecisionModalTitle",
              "Submit Booking Decision"
            )}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted mb-3">
              {t(
                "workerBookingRequestDecisionModalIntro",
                "Please select Accept or Reject for each pending date:"
              )}
            </p>
            {requestData?.WorkerShift?.filter(
              (shift) =>
                shift.status === "pending" &&
                requestData.status === "pending" &&
                !dayjs(shift.expiresAt).isBefore(dayjs())
            ).length === 0 ? (
              <div className="alert alert-info">
                {t(
                  "workerBookingRequestNoPendingDates",
                  "No pending dates available for decision."
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <Table bordered>
                  <thead className="table-light">
                    <tr>
                      <th>{t("workerBookingRequestDate", "Date")}</th>
                      <th>
                        {t("workerBookingRequestPerHourPrice", "Per Hour Price")}
                      </th>
                      <th>{t("workerBookingRequestExpiresAt", "Expires At")}</th>
                      <th>{t("workerBookingRequestDecision", "Decision")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestData?.WorkerShift?.filter(
                      (shift) =>
                        shift.status === "pending" &&
                        requestData.status === "pending" &&
                        !dayjs(shift.expiresAt).isBefore(dayjs())
                    ).map((shift, idx) => (
                      <tr key={idx}>
                        <td className="align-middle">
                          {dayjs(shift.date).format("DD MMM YYYY")}
                        </td>
                        <td className="align-middle">
                          £{parseFloat(shift.perHourPrice || 0).toFixed(2)}
                        </td>
                        <td className="align-middle">
                          {shift.expiresAt
                            ? dayjs(shift.expiresAt).format(
                                "DD MMM YYYY, HH:mm"
                              )
                            : "-"}
                        </td>
                        <td>
                          {canWorkerAcceptInviteDate(
                            requestData,
                            shift.date
                          ) ? (
                            <Input
                              type="select"
                              value={dateDecisions[shift.date] || "pending"}
                              onChange={(e) =>
                                handleDateDecision(shift.date, e.target.value)
                              }
                            >
                              <option value="pending">
                                {t(
                                  "workerBookingRequestSelectDecision",
                                  "Select Decision"
                                )}
                              </option>
                              <option value="accepted">
                                {t("workerBookingStatusAccepted", "Accepted")}
                              </option>
                              <option value="rejected">
                                {t("workerBookingStatusRejected", "Rejected")}
                              </option>
                            </Input>
                          ) : (
                            <div>
                              <Input
                                type="select"
                                value={dateDecisions[shift.date] || "rejected"}
                                onChange={(e) =>
                                  handleDateDecision(shift.date, e.target.value)
                                }
                              >
                                <option value="rejected">
                                  {t("workerBookingStatusRejected", "Rejected")}
                                </option>
                              </Input>
                              <small className="text-muted d-block mt-1">
                                {t(
                                  "workerBookingRequestNoSlotsRemaining",
                                  "No slots remaining for this date; only rejection is available."
                                )}
                              </small>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={() => setShowDecisionModal(false)}
              disabled={approveRequest.isPending}
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleSubmitDecisions}
              disabled={approveRequest.isPending}
            >
              {approveRequest.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("submitting", "Submitting...")}
                </>
              ) : (
                <>
                  <i className="mdi mdi-check me-1"></i>
                  {t("workerBookingRequestSubmitDecisions", "Submit Decisions")}
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Leave Application Modal */}
        <Modal
          isOpen={showLeaveModal}
          toggle={() => setShowLeaveModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowLeaveModal(false)}>
            {t("workerBookingRequestLeaveModalTitle", "Apply for Leave")}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label>
                {t("workerBookingRequestSelectDates", "Select Dates")}
              </Label>
              <div className="d-flex flex-wrap gap-2">
                {confirmedShifts.map((shift, idx) => {
                  const isSelected = leaveDates.includes(shift.date);
                  return (
                    <div
                      key={idx}
                      className={`d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative`}
                      style={{
                        cursor: "pointer",
                        minWidth: "100px",
                        backgroundColor: isSelected ? "#eef0f7" : "transparent",
                        borderColor: isSelected ? "#556ee6" : "#e9e9ef",
                        transition: "all 0.2s",
                      }}
                      onClick={() => toggleLeaveDate(shift.date)}
                    >
                      {isSelected && (
                        <div
                          className="position-absolute"
                          style={{ top: "5px", right: "5px" }}
                        >
                          <i className="mdi mdi-check-circle text-primary"></i>
                        </div>
                      )}
                      <h5
                        className={`font-size-16 mb-1 ${
                          isSelected ? "text-primary" : "text-body"
                        }`}
                      >
                        {dayjs(shift.date).format("DD")}
                      </h5>
                      <div
                        className={`font-size-13 ${
                          isSelected ? "text-primary" : "text-muted"
                        }`}
                      >
                        {dayjs(shift.date).format("MMM YYYY")}
                      </div>
                      <div className="font-size-11 text-muted mt-1">
                        {dayjs(shift.date).format("dddd")}
                      </div>
                    </div>
                  );
                })}
              </div>
              {confirmedShifts.length === 0 && (
                <div className="text-muted">
                  {t(
                    "workerBookingRequestNoConfirmedDates",
                    "No confirmed dates available to apply for leave."
                  )}
                </div>
              )}
            </div>

            <div className="mb-3">
              <Label htmlFor="leaveReason">
                {t("workerBookingRequestReason", "Reason")}
              </Label>
              <Input
                type="select"
                id="leaveReason"
                value={leaveReason}
                onChange={(e) => setLeaveReason(e.target.value)}
              >
                <option value="">
                  {t("workerBookingRequestSelectReason", "Select a reason")}
                </option>
                {Object.values(LEAVE_REASONS).map((reason) => (
                  <option key={reason} value={reason}>
                    {getLeaveReasonLabel(reason, t)}
                  </option>
                ))}
              </Input>
            </div>

            <div className="mb-3">
              <Label htmlFor="leaveDescription">
                {t("workerBookingRequestDescriptionLabel", "Description")}
              </Label>
              <Input
                type="textarea"
                id="leaveDescription"
                rows="3"
                placeholder={t(
                  "workerBookingRequestLeaveDescPlaceholder",
                  "Please provide more details about your leave request..."
                )}
                value={leaveDescription}
                onChange={(e) => setLeaveDescription(e.target.value)}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowLeaveModal(false)}
              disabled={applyForLeave.isPending}
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleSubmitLeave}
              disabled={applyForLeave.isPending}
            >
              {applyForLeave.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("submitting", "Submitting...")}
                </>
              ) : (
                t("workerBookingRequestSubmitLeave", "Submit Leave Request")
              )}
            </Button>
          </ModalFooter>
        </Modal>

        <BookingAgreementSignModal
          isOpen={showSignAgreementModal}
          toggle={() => setShowSignAgreementModal(false)}
          bookingRequestSlug={requestData?.slug}
          agreementPdf={requestData?.agreementPdf}
          pdfAxis={requestData?.pdfAxis ?? null}
          onSuccess={refetch}
        />
      </div>
    </div>
  );
};

export default withTranslation()(WorkerBookingRequestDetails);
